"use client";

import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useTranslations } from "next-intl";

export default function Devastator() {
  const t = useTranslations("davestator");

  // Startposition: Oben rechts in der Ecke - initial mit Dummy-Wert
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [direction, setDirection] = useState(0); // 0-7 für 8 Richtungen
  const [hasMovedOnce, setHasMovedOnce] = useState(false); // Track ob User schon gesteuert hat
  const [spinFrame, setSpinFrame] = useState(0); // Für Spin-Animation
  const [isInitialized, setIsInitialized] = useState(false); // Track ob Position gesetzt wurde
  const keysPressed = useRef<Set<string>>(new Set());
  const animationFrameRef = useRef<number | undefined>(undefined);
  const positionRef = useRef(position); // Ref für aktuelle Position

  // Position-Ref aktuell halten
  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  // Initiale Position setzen (client-side only)
  useEffect(() => {
    const updatePosition = () => {
      setPosition({ x: window.innerWidth - 120, y: 20 });
      if (!isInitialized) {
        setIsInitialized(true);
      }
    };

    // Initiale Position setzen
    updatePosition();

    // Position bei Resize anpassen
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [isInitialized]);

  // Spin-Animation solange nicht bewegt wurde
  useEffect(() => {
    if (hasMovedOnce) return;

    let lastTime = 0;
    const spinAnimation = (time: number) => {
      if (time - lastTime > 500) {
        // Alle 500ms Frame wechseln
        setSpinFrame((prev) => (prev + 1) % 8);
        lastTime = time;
      }
      if (!hasMovedOnce) {
        requestAnimationFrame(spinAnimation);
      }
    };

    const rafId = requestAnimationFrame(spinAnimation);
    return () => cancelAnimationFrame(rafId);
  }, [hasMovedOnce]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      const key = e.key.toLowerCase();
      if (["arrowup", "arrowdown", "arrowleft", "arrowright"].includes(key)) {
        e.preventDefault();
        keysPressed.current.add(key);
        if (!hasMovedOnce) {
          setHasMovedOnce(true); // Spin-Animation stoppen beim ersten Tastendruck
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.key) return;
      const key = e.key.toLowerCase();
      keysPressed.current.delete(key);
    };

    const updatePosition = () => {
      if (keysPressed.current.size === 0) {
        animationFrameRef.current = requestAnimationFrame(updatePosition);
        return;
      }

      const keys = keysPressed.current;
      let dx = 0;
      let dy = 0;

      // Pfeiltasten - schnellere Geschwindigkeit
      if (keys.has("arrowup")) dy -= 6;
      if (keys.has("arrowdown")) dy += 6;
      if (keys.has("arrowleft")) dx -= 12;
      if (keys.has("arrowright")) dx += 12;

      // Richtung bestimmen
      let newDirection = direction;
      if (dy < 0 && dx === 0)
        newDirection = 0; // Oben
      else if (dy < 0 && dx > 0)
        newDirection = 1; // Oben-Rechts
      else if (dy === 0 && dx > 0)
        newDirection = 2; // Rechts
      else if (dy > 0 && dx > 0)
        newDirection = 3; // Unten-Rechts
      else if (dy > 0 && dx === 0)
        newDirection = 4; // Unten
      else if (dy > 0 && dx < 0)
        newDirection = 5; // Unten-Links
      else if (dy === 0 && dx < 0)
        newDirection = 6; // Links
      else if (dy < 0 && dx < 0) newDirection = 7; // Oben-Links

      setDirection(newDirection);

      setPosition((prev) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollY = window.scrollY;
        const scrollHeight = document.documentElement.scrollHeight;
        const maxScroll = scrollHeight - viewportHeight;

        let newX = prev.x + dx;
        let newY = prev.y + dy;

        // Horizontal: Wrap-around (links raus = rechts rein)
        if (newX < -80) newX = viewportWidth;
        if (newX > viewportWidth) newX = -80;

        // Vertikal: Scrollen wenn am Rand
        if (dy !== 0) {
          // Nach unten fahren - Scroll wenn nahe am unteren Rand
          if (dy > 0 && newY >= viewportHeight - 200) {
            if (scrollY < maxScroll) {
              // Scrolle die Seite
              window.scrollBy({ top: 8, behavior: "instant" });
              // Bewege Panzer nicht weiter runter
              newY = viewportHeight - 200;
            } else {
              // Am Ende der Seite - normal weiterbewegen
              newY = Math.min(newY, viewportHeight - 100);
            }
          }

          // Nach oben fahren - Scroll wenn nahe am oberen Rand
          if (dy < 0 && newY <= 200) {
            if (scrollY > 0) {
              // Scrolle die Seite
              window.scrollBy({ top: -8, behavior: "instant" });
              // Bewege Panzer nicht weiter hoch
              newY = 200;
            } else {
              // Am Anfang der Seite - normal weiterbewegen
              newY = Math.max(newY, 20);
            }
          }
        }

        // Vertikal begrenzen
        newY = Math.max(20, Math.min(newY, viewportHeight - 100));

        return { x: newX, y: newY };
      });

      animationFrameRef.current = requestAnimationFrame(updatePosition);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    animationFrameRef.current = requestAnimationFrame(updatePosition);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [direction]);

  const getSpritePosition = (dir: number) => {
    return {
      backgroundPosition: `-${dir * 80}px 0px`,
    };
  };

  // Verwende spinFrame wenn noch nicht bewegt, sonst direction
  const currentFrame = hasMovedOnce ? direction : spinFrame;

  return (
    <a
      href="https://archive.org/details/msdos_Dune_2_-_The_Building_of_a_Dynasty_1992"
      target="_blank"
      rel="noreferrer noopener"
      className={`hidden lg:block z-[100] w-20 h-20 select-none transition-none ${hasMovedOnce ? "fixed top-30 left-30" : "absolute left-30 top-30"}`}
      style={{
        left: hasMovedOnce ? `${position.x}px` : undefined,
        top: hasMovedOnce ? `${position.y}px` : undefined,
        right: hasMovedOnce ? undefined : "2rem",
        bottom: hasMovedOnce ? undefined : "2rem",
      }}
    >
      <div
        className="w-20 h-20 cursor-pointer pointer-events-auto relative"
        style={{
          backgroundImage: "url('/davestator2.png')",
          backgroundSize: "640px 80px",
          imageRendering: "pixelated",
          ...getSpritePosition(currentFrame),
        }}
        title={t("tooltip")}
      >
        {!hasMovedOnce && (
          <>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 rounded-sm p-1 text-white transition-colors duration-300 ease-in-out animate-pulse">
              <ArrowUp size={16} />
            </div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 rounded-sm p-1 text-white transition-colors duration-300 ease-in-out animate-pulse">
              <ArrowDown size={16} />
            </div>
            <div className="absolute left-full top-1/2 -translate-y-1/2 rounded-sm p-1 text-white transition-colors duration-300 ease-in-out animate-pulse">
              <ArrowRight size={16} />
            </div>
            <div className="absolute right-full top-1/2 -translate-y-1/2 rounded-sm p-1 text-white transition-colors duration-300 ease-in-out animate-pulse">
              <ArrowLeft size={16} />
            </div>
          </>
        )}
      </div>
    </a>
  );
}
